import { Chip } from '@mui/material';
import { AnnouncementKind, ANNOUNCEMENT_KIND_LABELS } from 'types/auth';

interface AnnouncementTypeChipProps {
  type?: AnnouncementKind;
  // The list packs these into a 320px column; the detail header has room for a touch more.
  size?: 'compact' | 'regular';
}

/**
 * The type tag, shared by the list rows and the detail header so the two cannot drift.
 *
 * Both kinds get a chip, not only training. A tag that appears on one of two states reads
 * as a badge for that state and leaves the other looking untagged rather than
 * deliberately typed — which is the opposite of what a type filter needs the list to say.
 *
 * Falls back to 'announcement' for records written before the field existed, matching the
 * value the server defaults them to.
 */
// Training is a solid dark navy rather than MUI's `info`, which renders a pale cyan that
// reads as a status hint instead of a category. Set explicitly rather than through a
// palette slot because no existing token is this colour, and it wants to stay put if the
// theme's blues are ever retuned.
const TRAINING_BG = '#1E3A8A';

const AnnouncementTypeChip = ({ type, size = 'regular' }: AnnouncementTypeChipProps) => {
  const kind: AnnouncementKind = type === 'training' ? 'training' : 'announcement';
  const compact = size === 'compact';
  const isTraining = kind === 'training';

  return (
    <Chip
      label={ANNOUNCEMENT_KIND_LABELS[kind]}
      size="small"
      variant={isTraining ? 'filled' : 'outlined'}
      sx={{
        height: compact ? 18 : 20,
        borderRadius: 999,
        // Filled navy for training, quiet outline for an announcement. The weight
        // difference is the point: training is the exception worth spotting in a list that
        // is mostly release notes.
        ...(isTraining && {
          bgcolor: TRAINING_BG,
          color: '#fff',
          // Chips darken their own background on hover; the row underneath already owns
          // the hover feedback, so hold the colour steady.
          '&:hover': { bgcolor: TRAINING_BG }
        }),
        '& .MuiChip-label': { px: 0.75, fontSize: compact ? 10 : 11, fontWeight: 600 }
      }}
    />
  );
};

export default AnnouncementTypeChip;
